import {
  ComparablePlan as GlobalComparablePlan,
  ComparablePlanType,
  MedicarePlanType,
  MedicareQuoteFilterExtraBenefit,
  PdpPlan,
  Plan,
} from '@coverright/data-access/types/medicare';
import * as React from 'react';
import { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import sumBy from 'lodash/sumBy';
import { getQuoteId } from '@coverright/utils';

export const AppStateContext = React.createContext<
  [AppState, (values: Partial<AppState>) => Promise<void>, boolean]
>([
  {
    stepsCompleted: [],
  },
  async () => {},
  false
]);

export const AppStateContextProvider = (
  props: PropsWithChildren<{ quoteId?: string, userFlowRestUrl: string }>
) => {
  const [state, setState] = useState(
    JSON.parse(sessionStorage.getItem('appState') || '{}')
  );
  const [loaded, setLoaded] = useState(false);
  const prevQuoteId = useRef<string>()

  // The quote id is already the synchronous source of truth in storage (written by personal-info
  // when saveQuote resolves). Resolve it from there so this id-only consumer never depends on the
  // async, auth-gated, render-timing-sensitive `useMedicareQuote` chain to derive it. `props.quoteId`
  // stays as an override, so behavior is identical whenever it's set (the normal/working case).
  const quoteId = props.quoteId || getQuoteId();

  useEffect(() => {
    if (!prevQuoteId.current && quoteId) {
      prevQuoteId.current = quoteId;
      fetch(
        `${props.userFlowRestUrl}/getState/${quoteId}`
      ).then((data) => {
        if (data.status === 200) {
          return data.json();
        }
        return {state: {}}
      }).then(data => setState(data?.state || {}))
        .finally(() => setLoaded(true))
    }
  }, [quoteId, props.userFlowRestUrl]);

  const save = async (values: Partial<AppState>) => {
    // Read at CALL time (the user's action) so it reflects current storage — this provider can
    // mount before the quote exists and may not re-render, so a captured render-time value can be stale.
    const currentQuoteId = props.quoteId || getQuoteId();
    const prevState = { ...state };
    const newState = { ...state, ...values };

    setState(newState);

    try {
      if (!currentQuoteId) {
        throw new Error('Can\'t find a quote id' )
      }
      await saveStateToServer(props.userFlowRestUrl + '/saveState', currentQuoteId, newState);
    } catch (err) {
      setState(prevState);
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  };

  const value: [AppState, (values: Partial<AppState>) => Promise<void>, boolean] = useMemo(() => ([
    { ...state, medicareQuoteId: quoteId }, save, loaded
  ]), [state, quoteId, loaded]);

  return (
    <AppStateContext.Provider value={value}>
      {props.children}
    </AppStateContext.Provider>
  );
};

export const useAppContext = () => useContext(AppStateContext);

export function useSavedPlansCount() {
  const [{ maSavedPlans = [], mgSavedPlans = [], pdpSavedPlans = [] }] = useAppContext();
  return sumBy([maSavedPlans, mgSavedPlans, pdpSavedPlans], 'length');
}

const saveStateToServer = (url: string, quoteId: string, state: AppState) => {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      state,
      quoteId,
    }),
  });
};

export type ComparablePlan = GlobalComparablePlan & {
  aiSuggested?: boolean;
}

export interface ScoringFlags {
  // ── Doctors credit-gradient ───────────────────────────────────────────────
  doctorsSkipped?: boolean;          // user skipped the doctors gate question
  doctorsReconfirmed?: boolean;      // user answered No and confirmed ("I have no doctors")

  // ── Medications credit-gradient ───────────────────────────────────────────
  drugsSkipped?: boolean;
  drugsReconfirmed?: boolean;

  // ── Other section completion ──────────────────────────────────────────────
  pharmacySelected?: boolean;   // user explicitly selected a pharmacy preference
  coverageAnswered?: boolean;   // user answered the Other Coverage section
}

export interface AppState {
  situation?: string;
  stepsCompleted: boolean[];
  marketplaceRedirect?: ComparablePlanType;
  maSavedPlans?: ComparablePlan[];
  mgSavedPlans?: ComparablePlan[];
  pdpSavedPlans?: ComparablePlan[];
  benefits?: string[];
  programs?: string[];
  paymentPreference?: 'save_when_healthy' | 'predictable';
  planType?: MedicarePlanType;
  hasDrugs?: boolean;
  hasDoctors?: boolean;

  selectedPlans?: GlobalComparablePlan[];

  /** Scoring credit-gradient flags — grouped to keep AppState flat */
  scoring?: ScoringFlags;

  medicareQuoteId?: string;

  maVisited?: boolean;

  logoOverride?: {
    forbesHealth: boolean;
    benzinga: boolean;
    assetInsure: boolean;
    bellInsurance: boolean;
    rightsure: boolean;
    kroger: boolean;
    nerdWallet: boolean;
  };

  //--- legacy ---
  videosApplied?: boolean;
  plansShown?: boolean;
  advisorCall?: boolean;
  appointmentType?: number;
  selectedCoverage?: 'MA' | 'MG' | 'PDP';
  existingCoverage?: 'MA' | 'PDP';
  maCarrier?: string;
  mgCarrier?: string;
  pdpCarrier?: string;
  maCurrentPlan?: string;
  pdpCurrentPlan?: string;
  quizSelected?: number;
  quizSuggested?: string;
  quizSuggestedText?: string;
  quizCompleted?: boolean;
  quizSelectedText?: string;

  videosCompleted?: string[];
  callScheduled?: boolean;
  greetingShown?: boolean;
  forbesHealthLogo?: boolean;
  quizAnswers?: boolean[];
  overrideMAEnrollment?: Plan;
  overridePDPEnrollment?: PdpPlan;
  abTesting?: {
    [key: string]: 'a' | 'b' | 'ab';
  };
  additionalInfo?: string[];
}
